import type { WeBuildAiAdmin } from "@webuild-ai/admin";
import type { UserInput } from "@webuild-ai/admin/models/components";
import type { UserProfile } from "@webuild-ai/react-auth";

/**
 * The config fields that have a writer of their own, so they must survive every
 * other write to the user record.
 *
 * `updateUser` replaces the whole personal config, and each writer builds its
 * body from the profile it was handed. The tool preference sync never updates
 * the react-auth profile, and App.tsx's profile save sends whatever the profile
 * held at the time, so without this a language change could send stale tool
 * preferences, and a tool toggle could send the memory setting as it stood
 * before the person changed it.
 */
export interface DedicatedUserConfig {
  memoryEnabled?: boolean | null;
  toolPreferences?: NonNullable<UserInput["config"]>["toolPreferences"];
}

/**
 * How long one write may take before it is abandoned. Writes are queued, so
 * without a bound a single hung request would hold every later write, and the
 * memory switch would stay disabled with no message.
 */
const WRITE_TIMEOUT_MS = 30_000;

/** The dedicated fields, as a list, for callers that need to strip them from a diff. */
export const DEDICATED_CONFIG_FIELDS = ["memoryEnabled", "toolPreferences"] as const;

interface WriterState {
  userId: string | null;
  /** The last value of each dedicated field that this client saved successfully. */
  saved: DedicatedUserConfig;
  /** The tail of the write queue, so writes reach admin in the order they were made. */
  tail: Promise<unknown>;
}

const state: WriterState = { userId: null, saved: {}, tail: Promise.resolve() };

function savedFor(userId: string): DedicatedUserConfig {
  if (state.userId !== userId) {
    state.userId = userId;
    state.saved = {};
  }

  return state.saved;
}

/**
 * Write the user record, carrying every dedicated field at its latest value.
 *
 * Writes are queued, so each body is built only after the previous write has
 * settled and therefore always includes what that write saved. Later sources
 * override earlier ones: the profile, then values this client has saved, then
 * `patch`. A
 * dedicated field is recorded as saved only once admin accepts it, so a failed
 * write is not carried forward by the next one.
 *
 * Abandoning a write stops this client waiting for it, not admin applying it,
 * so a write that admin commits late can land after the next queued one. That
 * ordering risk is accepted in favour of never stalling the queue (ADR-0030).
 *
 * The returned promise rejects when this write fails or times out, so a caller can revert
 * its own optimistic state. A failure does not stop later writes in the queue.
 */
export function writeUserRecord(
  adminClient: WeBuildAiAdmin,
  profile: UserProfile,
  patch: DedicatedUserConfig = {},
): Promise<void> {
  const run = state.tail.then(async () => {
    const saved = savedFor(profile.id);
    const config = { ...(profile.config || {}), ...saved, ...patch };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), WRITE_TIMEOUT_MS);

    try {
      await adminClient.users.updateUser(
        { userId: profile.id, userInput: { ...profile, config } as UserInput },
        { signal: controller.signal },
      );
    } finally {
      clearTimeout(timer);
    }

    for (const field of DEDICATED_CONFIG_FIELDS) {
      if (field in patch) {
        (saved as Record<string, unknown>)[field] = patch[field];
      }
    }
  });

  state.tail = run.catch(() => undefined);

  return run;
}

/** Read the person's memory setting from a profile, accepting either wire spelling. */
export function readMemoryEnabled(profile: UserProfile | null): boolean | null {
  const config = profile?.config as Record<string, unknown> | undefined;
  // The camelCase key wins whenever it is present, so an explicit null there is
  // not overridden by a snake_case copy.
  const value = config && "memoryEnabled" in config ? config.memoryEnabled : config?.memory_enabled;

  return typeof value === "boolean" ? value : null;
}

/** Test seam: forget saved values and drain the queue. */
export function resetUserRecordWriterForTests(): void {
  state.userId = null;
  state.saved = {};
  state.tail = Promise.resolve();
}
